import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import * as ShopifyActions from '../../../actions/tools/ShopifyActions';
import * as ZendeskActions from '../../../actions/tools/ZendeskActions';
import * as ToolActions from '../../../actions/tools/ToolActions';
import * as OrdersSearchActions from '../../../actions/tools/OrdersSearchActions';
import * as ShipstationActions from '../../../actions/tools/ShipstationActions';
import CommonSelectTask from '../../../components/tools/CommonSelectTask/CommonSelectTask';
import { getExtraBusinessInfoOfSelectedTicket, isSpecialist } from '../../../extractFromState';
import * as ToolsSelectors from '../../../selectors/tools';
import { REPLACEMENT_SOURCE_NAME } from '../../../constants/shopify';
import * as sensitiveDataTypes from 'constants/sensitiveDataTypes';

const TASK_INPUTS = [
  {
    id: 'orderDetails',
    title: 'Order Summary',
  },
  {
    id: 'shippingAddress',
    title: 'Shipping Address',
    dataProtected: true,
    sensitiveDataType: sensitiveDataTypes.shippingAddress,
  },
  {
    id: 'cancelOrder',
    title: 'Cancel Order',
    conditionFunc: ({ selectedOrder, state }) => {
      return (
        !isReplacementOrder(selectedOrder) &&
        allowsCancellation(state) &&
        ToolsSelectors.getOrderModificationsAreAllowed(state)
      );
    },
  },
  {
    id: 'issueRefund',
    title: 'Issue Refund',
    conditionFunc: ({ selectedOrder, state }) => {
      return (
        !isReplacementOrder(selectedOrder) &&
        // isSpecialist(state) &&
        allowsRefund(state) &&
        isInvoiced(selectedOrder) &&
        isFulfillmentsExist(selectedOrder) &&
        ToolsSelectors.getOrderModificationsAreAllowed(state)
      );
    },
  },
  {
    id: 'issueRefundAmount',
    title: 'Refund a Given Dollar Amount',
    conditionFunc: ({ selectedOrder, state }) => {
      return (
        !isReplacementOrder(selectedOrder) &&
        isSpecialist(state) &&
        allowsRefund(state) &&
        isInvoiced(selectedOrder) &&
        ToolsSelectors.getOrderModificationsAreAllowed(state)
      );
    },
  },
  {
    id: 'returnOrder',
    title: 'Create a Return',
    conditionFunc: ({ selectedOrder, state }) => {
      return (
        !isReplacementOrder(selectedOrder) &&
        isLastMileCancellation(state) &&
        ToolsSelectors.getOrderModificationsAreAllowed(state)
      );
    },
  },
  {
    id: 'createReturnLabel',
    title: 'Create Return Label via Shipstation',
    conditionFunc: ({ selectedOrder, state }) => {
      return (
        returnLabelSupport(state) &&
        !isReplacementOrder(selectedOrder) &&
        ToolsSelectors.getOrderModificationsAreAllowed(state)
      );
    },
  },
  {
    id: 'replaceOrder',
    title: 'Send a Replacement',
    conditionFunc: ({ selectedOrder, state }) => {
      return !isReplacementOrder(selectedOrder) && ToolsSelectors.getOrderModificationsAreAllowed(state);
    },
  },
  {
    id: 'resendAccountCreationEmail',
    title: 'Resend Account Creation Email',
    conditionFunc: ({ selectedOrder }) => !isCustomerAccountEnabled(selectedOrder),
  },
];

function isInvoiced(selectedOrder) {
  return selectedOrder['financial_status'] !== 'pending';
}

function isFulfillmentsExist(selectedOrder) {
  return Boolean(selectedOrder.fulfillment_status);
}

function getShopifyConfig(state) {
  const selectedShop = ToolsSelectors.getSelectedShop(state);
  const selectedToolDomains = ToolsSelectors.selectSelectedToolDomains(state);
  const configs = selectedToolDomains['shopifyConfigs'] || {};

  return configs[selectedShop] || {};
}

function allowsRefund(state) {
  return allowsToFeature(state, 'refundConfig.allowsRefund');
}

function allowsCancellation(state) {
  return allowsToFeature(state, 'cancelConfig.allowsCancellation');
}

function allowsToFeature(state, access) {
  const config = getShopifyConfig(state);
  const allows = get(config, access, []);
  const specialist = isSpecialist(state);

  const canOpenSpecialist = specialist && allows.includes('specialist');
  const canOpenExpert = !specialist && allows.includes('expert');

  return config['cancelOrRefundSupport'] && (canOpenSpecialist || canOpenExpert);
}

function isReplacementOrder(selectedOrder) {
  return selectedOrder['source_name'] === REPLACEMENT_SOURCE_NAME;
}

function isLastMileCancellation(state) {
  const extraBusinessInfo = getExtraBusinessInfoOfSelectedTicket(state);

  return extraBusinessInfo.lastMileCancellation;
}

function returnLabelSupport(state) {
  const selectedToolDomains = ToolsSelectors.selectSelectedToolDomains(state);

  return get(selectedToolDomains, 'shipstationConfig.returnLabelSupport', false);
}

function isCustomerAccountEnabled(selectedOrder) {
  const customerState = get(selectedOrder, 'customer.state', null);

  return customerState === 'enabled';
}

function mapStateToProps(state) {
  const selectedToolDomains = ToolsSelectors.selectSelectedToolDomains(state);
  const selectedOrder = ToolsSelectors.getSelectedOrder(state);

  const taskInputsToShow = TASK_INPUTS.filter((taskInput) => {
    return taskInput.conditionFunc ? taskInput.conditionFunc({ selectedOrder, state }) : true;
  });

  return {
    selectedOrder,
    taskInputs: taskInputsToShow,
    selectedToolDomains,
  };
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    Object.assign({}, ShopifyActions, ZendeskActions, ToolActions, OrdersSearchActions, ShipstationActions),
    dispatch
  );
};

const ShopifySelectTaskContainer = connect(mapStateToProps, mapDispatchToProps)(CommonSelectTask);

export default ShopifySelectTaskContainer;
